import { UsermanagementApi } from '@liks/lac-svc-api/esm/apis/UsermanagementApi';
import type {
  Tenant,
  TenantUsersDTO,
  User,
} from '@liks/lac-svc-api/esm/models/index';
import { lacTenantService, tenantDisplayName } from './LacTenantService';
import { lacApiConfiguration } from './lacApi';
import { calendarValidityToIsoRange } from '../utils/dateTimeUtils';
import {
  buildLacUserUpdatePayload,
  toLacEnabledUserPayload,
} from './lacUserServicePayloads';
import {
  setUserRolesWithRetry,
  waitUntilUserReadable,
} from './lacUserServiceProvisioning';
import type {
  LacCreateUserInput,
  LacCreateUserResult,
  LacResetUserPasswordInput,
  LacSetUserEnabledInput,
  LacTenantUsersGroup,
  LacUpdateUserInput,
  LacUser,
} from './lacUserServiceTypes';

export type {
  LacCreateUserInput,
  LacCreateUserResult,
  LacResetUserPasswordInput,
  LacSetUserEnabledInput,
  LacTenantUsersGroup,
  LacUpdateUserInput,
  LacUser,
} from './lacUserServiceTypes';

function compareLocale(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

class LacUserService {
  private readonly usermanagementApi = new UsermanagementApi(
    lacApiConfiguration,
  );

  private async listUsersGroupedByTenant(): Promise<TenantUsersDTO[]> {
    return this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementOverviewResourceListUsersByTenant();
  }

  async listUsersByTenant(): Promise<LacTenantUsersGroup[]> {
    const [tenants, groupedUsers] = await Promise.all([
      lacTenantService.listAllTenants(),
      this.listUsersGroupedByTenant(),
    ]);

    const tenantsByName = new Map<string, Tenant>();
    for (const tenant of tenants) {
      tenantsByName.set(tenant.name, tenant);
    }

    return groupedUsers
      .flatMap((group): LacTenantUsersGroup[] => {
        const tenantName = group.tenantId;
        if (!tenantName) {
          return [];
        }

        const tenant = tenantsByName.get(tenantName);
        const displayName = tenant ? tenantDisplayName(tenant) : tenantName;
        const users = [...(group.users ?? [])].sort((a, b) =>
          compareLocale(a.name, b.name),
        );

        return [
          {
            tenantName,
            displayName,
            users,
            totalUserCount: users.length,
            maxUsers: tenant?.maxUsers ?? null,
            logo: tenant?.logo ?? null,
          },
        ];
      })
      .sort((a, b) => compareLocale(a.displayName, b.displayName));
  }

  async createUser(input: LacCreateUserInput): Promise<LacCreateUserResult> {
    const tenantName = input.tenantName.trim();

    if (input.createTenant) {
      await lacTenantService.createTenant({ name: tenantName });
    }

    const validity = calendarValidityToIsoRange(input);

    const user =
      await this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceCreateUser(
        {
          tenantName,
          userCreateRequest: {
            username: input.username,
            email: input.email,
            firstName: input.firstName,
            lastName: input.lastName,
            sendPasswordSetupEmail: input.sendInviteEmail,
            ...validity,
          },
        },
      );

    const userRef =
      user.id?.trim() ||
      (user.name.trim() !== '' ? user.name.trim() : input.username.trim());
    await waitUntilUserReadable(this.usermanagementApi, tenantName, userRef);

    try {
      await setUserRolesWithRetry(
        (tn, ref, roles) => this.setUserRoles(tn, ref, roles),
        tenantName,
        userRef,
        [input.role],
      );
      return { user, roleAssigned: true };
    } catch {
      return { user, roleAssigned: false };
    }
  }

  async getUser(tenantName: string, userRef: string): Promise<LacUser> {
    return this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceReadUser(
      {
        tenantName,
        userRef,
      },
    );
  }

  async getUserRoles(tenantName: string, userRef: string): Promise<string[]> {
    return this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceReadUserRoles(
      {
        tenantName: tenantName.trim(),
        userRef,
      },
    );
  }

  async updateUser(input: LacUpdateUserInput): Promise<User> {
    return this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceUpdateUser(
      {
        tenantName: input.tenantName.trim(),
        userRef: input.userRef,
        keycloakUserData: buildLacUserUpdatePayload(input),
      },
    );
  }

  async setUserEnabled(input: LacSetUserEnabledInput): Promise<User> {
    return this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceUpdateUser(
      {
        tenantName: input.tenantName.trim(),
        userRef: input.userRef,
        keycloakUserData: toLacEnabledUserPayload(input.profile, input.enabled),
      },
    );
  }

  async resetUserPassword(input: LacResetUserPasswordInput): Promise<void> {
    await this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceResetUserPassword(
      {
        tenantName: input.tenantName.trim(),
        userRef: input.userRef,
        userPasswordResetRequest: {
          value: input.password,
          temporary: input.temporary ?? true,
        },
      },
    );
  }

  async downloadUserPhoto(
    tenantName: string,
    userRef: string,
  ): Promise<Blob | null | undefined> {
    return this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceDownloadUserPhoto(
      {
        tenantName: tenantName.trim(),
        userRef,
      },
    );
  }

  async uploadUserPhoto(
    tenantName: string,
    userRef: string,
    file: File,
  ): Promise<void> {
    await this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceUploadUserPhoto(
      {
        tenantName: tenantName.trim(),
        userRef,
        data: file,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      },
    );
  }

  async deleteUserPhoto(tenantName: string, userRef: string): Promise<void> {
    await this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceDeleteUserPhoto(
      {
        tenantName: tenantName.trim(),
        userRef,
      },
    );
  }

  async setUserRoles(
    tenantName: string,
    userRef: string,
    roleNames: string[],
  ): Promise<void> {
    await this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceSetUserRoles(
      {
        tenantName: tenantName.trim(),
        userRef,
        userRolesDTO: { roleNames },
      },
    );
  }

  async deleteUser(tenantName: string, userRef: string): Promise<void> {
    await this.usermanagementApi.deLiksUsermanagementBoundaryUsermanagementResourceDeleteUser(
      {
        tenantName: tenantName.trim(),
        userRef,
      },
    );
  }
}

export const lacUserService = new LacUserService();
